import { useState, useEffect, useCallback, useRef } from 'react';
import cx from 'classnames';
import formatPrice from '@bwoty-web/price-formatter';
import PropTypes from 'prop-types';
import { useIsGlobetrotter } from '@bwoty-web/ui-kit/hooks';
import {
  Button,
  Tag,
  Rating,
  TripadvisorRating,
  GuestRating,
  Link,
} from '@bwoty-web/ui-kit';
import Title from '../../common/title';
import Subtitle from '../../common/subtitle';
import KeySellingPointList from '../../common/keySellingPointList';
import { updateUrlWithOpenHotelSection } from '../../utils/url';
import {
  isVerticalTopPositionPastElement,
  isVerticalBottomPositionPastElement,
} from '../../utils/elements';
import Overview from './sections/overview/overview';
import Navigation from './components/navigation';
import LikeButton from './components/likeButton';
import RoomTypePopUp from './sections/accommodation/roomTypePopup';
import SectionContentContainer from './sections/sectionContentContainer';
import queryHelper from '../../utils/url/queryHelper';
import { getRoomTypeInfoByWvIdAndRoomTypeKey } from '../../services/HotelsService';
import { scrollToSectionStart } from '../../utils/scroll';
import { sectionKeys } from './sections/helper';
import './hotelInfoView.scss';
import './hotelInfoBlock.scss';

const HotelInfoView = ({
  hotelContent,
  labels,
  siteSettings,
  isConceptHotel = false,
  initialSelectedSection = 'overview',
  departureMonth,
  hotelPrice = '',
  isMobile = false,
  popUpPage = false,
  isPackagePlusPage = false,
  showPartInfoRoomList,
}) => {
  const isFirstRender = useRef(true);
  const [selectedSectionKey, setSelectedSectionKey] = useState(
    initialSelectedSection,
  );
  const [openRoomTypeKey, setOpenRoomTypeKey] = useState();
  const [openedRoomTypeDataDictionary, updateOpenedRoomTypeDataDictionary] =
    useState({});

  const [stickyCTAIsVisible, setStickyCTAIsVisible] = useState(false);
  const [isNavSticky, setIsNavSticky] = useState(false);
  const hotelNavigationElementRef = useRef(null);
  const navSentinelRef = useRef(null);

  const handleScroll = useCallback(() => {
    const hotelInfoTopContainer = document.getElementsByClassName(
      'gw-hotel-info-ratings',
    )[0];
    const isPastHotelInfoTopContainer =
      hotelInfoTopContainer &&
      isVerticalTopPositionPastElement(hotelInfoTopContainer);
    const priceMatrixHeading = document.getElementById('price-list');
    const isPastPriceMatrixHeading =
      priceMatrixHeading &&
      isVerticalBottomPositionPastElement(priceMatrixHeading);

    setStickyCTAIsVisible(
      isMobile && !isPastPriceMatrixHeading && isPastHotelInfoTopContainer,
    );
  }, [stickyCTAIsVisible]);

  useEffect(() => {
    const hotelInfoTopContainer = document.getElementsByClassName(
      'gw-hotel-info-ratings',
    )[0];
    const isPastHotelInfoTopContainer =
      hotelInfoTopContainer &&
      isVerticalTopPositionPastElement(hotelInfoTopContainer);

    const priceMatrixHeading = document.getElementById('price-list');
    const isPastPriceMatrixHeading =
      priceMatrixHeading &&
      isVerticalBottomPositionPastElement(priceMatrixHeading);
    if (isMobile && !isPastPriceMatrixHeading && isPastHotelInfoTopContainer) {
      setStickyCTAIsVisible(true);
    }
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  useEffect(() => {
    const sentinel = navSentinelRef.current;
    if (!sentinel) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const isAboveViewport = entry.boundingClientRect.top < 0;
        setIsNavSticky(!entry.isIntersecting && isAboveViewport);
      },
      { threshold: [0] },
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, []);

  const sectionKeyChangeHandler = (key) => setSelectedSectionKey(key);

  useEffect(() => {
    updateUrlWithOpenHotelSection(selectedSectionKey);
    if (isFirstRender.current) isFirstRender.current = false;
    else scrollToSectionStart();
  }, [selectedSectionKey]);

  const {
    name,
    geographical,
    ratings,
    tags,
    sections,
    meta,
    nonConceptAccommodation,
    facts,
  } = hotelContent;

  const countryName = geographical?.country?.name || '';
  const areaName = geographical?.area?.name || '';

  // TODO: Ta bort hasTripSummary efter A/B-test
  const { baseUrl, idunUrl, siteId, hasTripSummary, heartMode } = siteSettings;

  useEffect(() => {
    const requestId = window.idunRequestId || '';
    const dataChannelName = `trip-summary-${requestId}`;
    const readyChannelName = `trip-summary-ready-${requestId}`;

    const dataChannel = new BroadcastChannel(dataChannelName);
    const readyChannel = new BroadcastChannel(readyChannelName);

    const handleReady = (event) => {
      if (event.data !== true) return;

      dataChannel.postMessage({
        areaName,
        countryName,
        hotelName: name,
      });
    };

    readyChannel.addEventListener('message', handleReady);

    return () => {
      readyChannel.removeEventListener('message', handleReady);
      readyChannel.close();
      dataChannel.close();
    };
  }, [areaName, countryName, name]);

  // Filter out sustainability section for non-concept hotels
  const filteredSections = sections.filter(
    (section) => section.key !== 'sustainability' || isConceptHotel,
  );

  const { wvId } = meta;

  const toggleRoomInfo = useCallback(
    (key) => {
      if (key && !openedRoomTypeDataDictionary[key]) {
        if (isConceptHotel) {
          getRoomTypeInfoByWvIdAndRoomTypeKey(baseUrl, siteId, wvId, key).then(
            (res) => {
              updateOpenedRoomTypeDataDictionary({
                ...openedRoomTypeDataDictionary,
                [key]: res,
              });
            },
          );
        } else {
          const parseKey = parseInt(key, 10);
          const roomType = nonConceptAccommodation?.roomTypes?.find(
            (r) =>
              (r.wvId && r.wvId === parseKey) ||
              (r.externalId && r.externalId === key),
          );
          if (!roomType) return;
          updateOpenedRoomTypeDataDictionary({
            ...openedRoomTypeDataDictionary,
            [key]: roomType,
          });
        }
      }
      queryHelper.updateQueryWithValues({ roomTypeKey: key ? `${key}` : null });
      setOpenRoomTypeKey(`${key}`);
    },
    [
      openedRoomTypeDataDictionary,
      isConceptHotel,
      baseUrl,
      siteId,
      wvId,
      nonConceptAccommodation?.roomTypes,
    ],
  );

  useEffect(() => {
    const key =
      decodeURIComponent(queryHelper.getQueryKeyValue('roomTypeKey')) || 0;
    toggleRoomInfo(key);
  }, [toggleRoomInfo]);

  const messageEventHandler = (event) => {
    if (event.origin === window.origin) {
      if (typeof event.data === 'string') {
        const message = event.data.split(':');
        if (message[0] === 'cmd.open.popup' && message.length > 1) {
          toggleRoomInfo(message[1]);
        }
      }
    }
  };

  // In order to be able to open hotelPushes and the popup from the priceMatrix app
  /* eslint-disable consistent-return */
  useEffect(() => {
    if (typeof window !== 'undefined' && window) {
      window.addEventListener('message', messageEventHandler);

      return () => window.removeEventListener('message', messageEventHandler);
    }
  }, [toggleRoomInfo]);

  const [selectedSection, selectedSubSection] = selectedSectionKey.split(':');
  const sectionHeadingDict = Object.fromEntries(
    filteredSections.map((x) => [x.key, x.heading]),
  );
  const isGlobe = useIsGlobetrotter();

  return (
    <div className="gw-hotel-info">
      {heartMode && (
        <LikeButton
          idunUrl={idunUrl}
          wvId={wvId}
          siteId={siteId}
          hotelContent={hotelContent}
        />
      )}
      <div className="gw-hotel-info__top uikit-box">
        {tags?.length > 0 && !isGlobe && (
          <div className="gw-hotel-info__tags">
            <Tag small>{tags[0]?.text}</Tag>
          </div>
        )}
        <div className="gw-hotel-info__top-wrapper">
          <div className="gw-hotel-info__top-description">
            <Title>{name}</Title>
            <Subtitle geographical={geographical} />
            <div className="gw-hotel-info__ratings">
              <Rating rating={ratings.standard} size="s" responsive />
              <TripadvisorRating
                rating={ratings?.tripAdvisor?.value}
                onClick={() => setSelectedSectionKey(`${sectionKeys.rating}:1`)}
                showScale
              />
              <GuestRating
                rating={ratings?.guests}
                onClick={() => setSelectedSectionKey(`${sectionKeys.rating}:0`)}
                showScale
                showLabel
                inlineLabel
              />
            </div>
            <KeySellingPointList
              ksps={facts?.usps}
              className="gw-hotel-info__ksps"
              checkmark={!isGlobe}
            />
          </div>
          {!isPackagePlusPage && !hasTripSummary && (
            <div className="gw-hotel-info__top-book">
              {hotelPrice && (
                <div className="gw-hotel-info__price-wrapper">
                  <span className="gw-hotel-info__price-prefix">
                    {labels?.hotelPrice}
                  </span>
                  <span
                    className={cx('gw-hotel-info__price', {
                      'gw-hotel-info__price--red': ![1, 18].includes(siteId),
                    })}
                  >
                    {formatPrice(hotelPrice, siteId, true)}
                  </span>
                </div>
              )}
              {!popUpPage && (
                <Button variant="primary" href="#price-list">
                  {labels?.seeAvailabilityBtnText}
                </Button>
              )}
            </div>
          )}
          {!popUpPage && !isPackagePlusPage && hasTripSummary && (
            <Link className="gw-hotel-info__top-skiplink" href="#price-list">
              {labels?.seeAvailabilityBtnText}
            </Link>
          )}
        </div>
      </div>
      <div
        className={cx('gw-hotel-info-container', 'uikit-box', {
          'gw-hotel-info-container--concept': isConceptHotel,
        })}
        id="gw-hotel-info-top"
        itemScope
        itemType="https://schema.org/Hotel"
      >
        <div
          ref={navSentinelRef}
          style={{ position: 'absolute', top: 0, height: '1px', width: '1px' }}
        />
        <div
          className={cx('gw-hotel-info__nav-container', {
            'gw-hotel-info__nav-container--sticky': isNavSticky,
          })}
        >
          <Navigation
            sections={filteredSections}
            selectedSectionKey={selectedSectionKey}
            changeSection={sectionKeyChangeHandler}
            labels={labels}
            isGlobe={isGlobe}
            tightNavPosition={hasTripSummary}
          />
        </div>
        <div
          ref={hotelNavigationElementRef}
          className="gw-hotel-info__main-container"
          id="gw-hotel-info-content"
        >
          <Overview
            hotelContent={hotelContent}
            labels={labels}
            siteSettings={siteSettings}
            changeSection={sectionKeyChangeHandler}
            isActive={selectedSection === 'overview'}
            isConceptHotel={isConceptHotel}
            sectionHeadingDict={sectionHeadingDict}
            departureMonth={departureMonth}
            isGlobe={isGlobe}
          />
          {filteredSections.map((section) => {
            return (
              <SectionContentContainer
                labels={labels}
                key={section.key}
                section={section}
                selectedSubSection={
                  selectedSubSection && parseInt(selectedSubSection, 10)
                }
                siteSettings={siteSettings}
                wvId={wvId}
                isActive={selectedSection === section.key}
                toggleRoomInfo={toggleRoomInfo}
                changeSelectedSection={sectionKeyChangeHandler}
                nonConceptAccommodation={nonConceptAccommodation}
              />
            );
          })}
        </div>
        {openRoomTypeKey && openedRoomTypeDataDictionary[openRoomTypeKey] && (
          <RoomTypePopUp
            isConceptHotel={isConceptHotel}
            siteSettings={siteSettings}
            generalUspList={nonConceptAccommodation.allRoomsUspLists[0]}
            roomType={openedRoomTypeDataDictionary[openRoomTypeKey]}
            accommodationSection={filteredSections.find(
              (section) => section.key === 'accommodation',
            )}
            toggleRoomInfo={toggleRoomInfo}
            showPartInfoRoomList={showPartInfoRoomList}
          />
        )}
        {stickyCTAIsVisible && (
          <div className="gw-hotel-info__sticky-cta-mobile">
            <Button variant="primary" href="#price-list">
              {labels?.seeAvailabilityBtnText}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

HotelInfoView.propTypes = {
  hotelContent: PropTypes.shape({
    name: PropTypes.string,
    facts: PropTypes.shape({
      all: PropTypes.arrayOf(PropTypes.shape({})),
      usps: PropTypes.arrayOf(PropTypes.string),
    }),
    geographical: PropTypes.shape({
      country: PropTypes.shape({
        name: PropTypes.string,
      }),
      area: PropTypes.shape({
        name: PropTypes.string,
      }),
    }),
    ratings: PropTypes.shape({
      standard: PropTypes.number,
      guests: PropTypes.number,
      ratingDetails: PropTypes.shape({
        numberOfAnswers: PropTypes.number,
      }),
      tripAdvisor: PropTypes.shape({
        numberOfReviews: PropTypes.number,
        value: PropTypes.number,
      }),
      officialRating: PropTypes.string,
    }),
    tags: PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string,
      }),
    ),
    sections: PropTypes.arrayOf(PropTypes.shape()).isRequired,
    selectedSection: PropTypes.string,
    meta: PropTypes.shape({
      wvId: PropTypes.string,
    }),
    imageList: PropTypes.arrayOf(PropTypes.shape).isRequired,
    nonConceptAccommodation: PropTypes.shape({
      roomTypes: PropTypes.arrayOf(PropTypes.shape({})),
      allRoomsUspLists: PropTypes.arrayOf(PropTypes.shape({})),
    }),
  }).isRequired,
  siteSettings: PropTypes.shape({
    siteId: PropTypes.number,
    baseUrl: PropTypes.string,
    imageDomain: PropTypes.string,
    idunUrl: PropTypes.string,
    hasTripSummary: PropTypes.bool,
    heartMode: PropTypes.bool,
  }).isRequired,
  labels: PropTypes.shape({
    seeAvailabilityBtnText: PropTypes.string.isRequired,
    hotelPrice: PropTypes.string,
  }).isRequired,
  isConceptHotel: PropTypes.bool,
  showPartInfoRoomList: PropTypes.bool.isRequired,
  initialSelectedSection: PropTypes.string,
  departureMonth: PropTypes.number.isRequired,
  hotelPrice: PropTypes.string,
  popUpPage: PropTypes.bool,
  isPackagePlusPage: PropTypes.bool,
  isMobile: PropTypes.bool,
};

export default HotelInfoView;
